'use client';

import { isVerticalAspectRatio } from '@app/(darkHeader)/ai/_shared/_utils/videoMetadata';
import { useEffect, useState } from 'react';

import { type VideoMetadata } from '../../FilmStripSlider';

/** Give up rather than leave the trim UI waiting on a source that never reports its metadata. */
const METADATA_LOAD_TIMEOUT_MS = 5000;

interface UseVideoEditMetadataArgs {
    videoSrc: string;
    /** Orientation comes from the search result; the element only reports pixel dimensions. */
    aspectRatio: number | undefined;
}

interface UseVideoEditMetadataReturn {
    /** Null until the probe resolves, which the filmstrip renders as its own loading state. */
    videoMetadata: VideoMetadata | null;
    isLoadingMetadata: boolean;
    error: string | null;
}

/**
 * Reads duration and frame dimensions off a detached `<video>`, because search results carry
 * neither width nor height. Mirrors the metadata load in the Stockblocks video editor.
 */
export function useVideoEditMetadata({ videoSrc, aspectRatio }: UseVideoEditMetadataArgs): UseVideoEditMetadataReturn {
    const [videoMetadata, setVideoMetadata] = useState<VideoMetadata | null>(null);
    const [isLoadingMetadata, setIsLoadingMetadata] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setVideoMetadata(null);
        setError(null);
        setIsLoadingMetadata(true);

        if (!videoSrc) return;

        let isCurrent = true;
        const video = document.createElement('video');

        function cleanup(): void {
            clearTimeout(timeout);
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('error', handleError);
        }

        function fail(message: string): void {
            if (!isCurrent) return;
            cleanup();
            console.error('Video metadata loading failed:', message, videoSrc);
            setError(message);
            setIsLoadingMetadata(false);
        }

        function handleLoadedMetadata(): void {
            if (!isCurrent) return;
            cleanup();
            setVideoMetadata({
                duration: video.duration,
                width: video.videoWidth,
                height: video.videoHeight,
                isVertical: isVerticalAspectRatio(aspectRatio),
            });
            setIsLoadingMetadata(false);
        }

        function handleError(): void {
            fail('Video failed to load');
        }

        // Declared after the handlers so `cleanup` can clear it; hoisting makes the reverse work too.
        const timeout = setTimeout(() => fail('Video metadata load timeout'), METADATA_LOAD_TIMEOUT_MS);

        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('error', handleError);
        video.preload = 'metadata';
        video.muted = true;
        video.src = videoSrc;

        return () => {
            isCurrent = false;
            cleanup();
        };
    }, [videoSrc, aspectRatio]);

    return { videoMetadata, isLoadingMetadata, error };
}
